import React, { useState, useEffect } from 'react';
import { Typography, theme, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import RuleList from './components/RuleManager/RuleList';
import RuleForm from './components/RuleManager/RuleForm';
import AdRuleAPIService from './services/AdRuleAPIService';
import './AdsOptimization.css';
import { ExperimentOutlined } from '@ant-design/icons';

const { Title } = Typography;

const AdsOptimization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const [ruleFormVisible, setRuleFormVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check if we need to show edit form on component load
  useEffect(() => {
    if (location.state?.editRuleId) {
      handleLoadRuleForEdit(location.state.editRuleId);
      
      // Clear the state to prevent showing form again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load rule for editing
  const handleLoadRuleForEdit = async (ruleId) => {
    try {
      if (ruleId.startsWith('new_') || ruleId.startsWith('temp_')) {
        // Handle new/temp rules
        const newRule = {
          id: ruleId,
          name: 'New Rule',
          description: '',
          type: 'budget_optimization',
          platform: 'google_ads',
          priority: 1,
          frequency: 'daily',
          conditions: {},
          actions: {}
        };
        setEditingRule(newRule);
        setRuleFormVisible(true);
      } else {
        // Load existing rule
        const response = await AdRuleAPIService.getRule(ruleId);
        if (response.success) {
          setEditingRule(response.data);
          setRuleFormVisible(true);
        } else {
          throw new Error(response.message);
        }
      }
    } catch (error) {
      console.error('Error loading rule:', error);
      message.error('Lỗi khi tải rule: ' + error.message);
    }
  };

  const containerStyle = {
    margin: '10px',
    padding: '16px',
    minHeight: 'calc(100vh - 80px)',
  };

  // Handle creating new rule - open modal to create rule first
  const handleCreateRule = () => {
    setEditingRule(null); // Ensure we're creating new, not editing
    setRuleFormVisible(true);
  };

  // Handle editing existing rule
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleFormVisible(true);
  };

  // Handle running a rule
  const handleRunRule = async (rule) => {
    try {
      message.loading({ content: 'Đang chạy rule...', key: 'runRule' });
      
      // TODO: Implement rule execution logic
      // For now, just show success message
      setTimeout(() => {
        message.success({ content: 'Rule đã chạy thành công!', key: 'runRule' });
      }, 2000);
      
      console.log('Running rule:', rule);
    } catch (error) {
      console.error('Error running rule:', error);
      message.error({ content: 'Lỗi khi chạy rule: ' + error.message, key: 'runRule' });
    }
  };

  // Handle saving rule
  const handleSaveRule = async (savedRule) => {
    console.log('Rule saved:', savedRule);
    
    // Trigger refresh of rule list
    setRefreshTrigger(prev => prev + 1);
    
    return savedRule;
  };

  // Handle editing flow for a rule - navigate to builder
  const handleEditFlow = (rule) => {
    if (rule.id && !rule.id.startsWith('new_') && !rule.id.startsWith('temp_')) {
      navigate(`/ads-tool/builder/${rule.id}`);
    } else {
      // For new rules, save first then navigate
      message.info('Vui lòng lưu rule trước khi chỉnh sửa workflow');
    }
  };

  // Handle viewing flow for a rule - also navigate to builder
  const handleViewFlow = (rule) => {
    console.log('Viewing flow for rule:', rule);
    
    // Check if rule has workflow data (either from workflow relation or workflowId)
    if (rule.workflow?.id || rule.workflowId) {
      navigate(`/ads-tool/builder/${rule.id}`);
    } else {
      message.warning('Rule này chưa có workflow. Bạn có muốn tạo workflow cho rule này không?', 5);
      // Optionally navigate to create workflow
      setTimeout(() => {
        navigate(`/ads-tool/builder/${rule.id}`);
      }, 2000);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <Title level={2} style={{ margin: 0, color: '#f97316' }}>
          <ExperimentOutlined /> Ads Optimization Rules
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '16px' }}>
          Manage your optimization rules and create data transformation flows
        </p>
      </div>
      
      <RuleList
        onCreateRule={handleCreateRule}
        onEditRule={handleEditRule}
        onRunRule={handleRunRule}
        onViewFlow={handleViewFlow}
        refreshTrigger={refreshTrigger}
      />

      {/* Rule Form Modal */}
      <RuleForm
        visible={ruleFormVisible}
        onCancel={() => {
          setRuleFormVisible(false);
          setEditingRule(null);
        }}
        onSave={handleSaveRule}
        editingRule={editingRule}
        onEditFlow={handleEditFlow}
      />
    </div>
  );
};

export default AdsOptimization;